import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Loader2, QrCode, AlertTriangle, SendHorizontal, Copy, Check, Share2, Download } from 'lucide-react';
import ModalShell from '../ui/ModalShell';
import { ACCENT as SECTION_COLOR, OK, WARN } from '../../lib/uiTokens';

/**
 * QR platba k dokladu — nafotí se bankovní appkou a platba je vyplněná.
 *
 * Data skládá backend (`QrPlatba::sestav`, formát SPAYD). Podstatné je pole
 * „Zpráva": začíná kódem `FL{id}`. Česká spořitelna ho vrátí ve výpisu
 * (prvních 20 znaků zprávy) a import podle něj pohyb sám spáruje s tímhle
 * dokladem — i bez VS nebo při dvou fakturách se stejnou částkou.
 *
 * **Na mobilu** se vlastní displej naskenovat nedá — QR jde proto uložit do
 * fotek nebo rovnou nasdílet (Web Share API). Bankovní appky (George, KB,
 * ČSOB, Air Bank…) umí QR platbu načíst z obrázku v galerii.
 *
 * Kdo zprávu v bankovní appce přepíše, o kód přijde; párování pak běží jako
 * dřív podle VS a částky.
 */

const fmt = (v) => (v == null ? '—'
    : new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v));
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('cs-CZ') : '—');
const fmtIban = (s) => (s ? s.replace(/(.{4})/g, '$1 ').trim() : null);

export default function QrPlatbaModal({ doklad, isDark, onClose, onPlatbaZadana }) {
    const [data, setData] = useState(null);
    const [chyba, setChyba] = useState(null);
    const [obrazek, setObrazek] = useState(null);
    const [zkopirovano, setZkopirovano] = useState(null);
    const [zadavam, setZadavam] = useState(false);

    const hranice = isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)';

    useEffect(() => {
        let zivy = true;
        fetch(`/api/finance/doklady/${doklad.id}/qr-platba`, { credentials: 'same-origin' })
            .then(r => (r.ok ? r.json() : Promise.reject(r.status)))
            .then(d => { if (zivy) setData(d); })
            .catch(() => zivy && setChyba('QR platbu se nepodařilo načíst.'));
        return () => { zivy = false; };
    }, [doklad.id]);

    useEffect(() => {
        if (!data?.spayd) { setObrazek(null); return; }
        // Bílý podklad a tichá zóna jsou podmínka čitelnosti, ne vzhled —
        // na tmavém pozadí appky QR nenačtou.
        QRCode.toDataURL(data.spayd, { errorCorrectionLevel: 'M', margin: 2, width: 560, color: { dark: '#000000', light: '#ffffff' } })
            .then(setObrazek)
            .catch(() => setChyba('QR kód se nepodařilo vykreslit.'));
    }, [data?.spayd]);

    // Telefon / tablet — tam je sdílení obrázku hlavní cesta, ne skenování
    const dotykove = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;

    // Sdílení obrázku (iOS/Android nabídne Uložit obrázek i bankovní appky),
    // jinde stažení PNG. Zrušené sdílení není chyba.
    const sdilejQr = async () => {
        if (!obrazek || !data) return;
        const nazev = `QR-platba-${data.kod}.png`;
        try {
            const blob = await (await fetch(obrazek)).blob();
            const soubor = new File([blob], nazev, { type: 'image/png' });
            if (navigator.canShare?.({ files: [soubor] })) {
                await navigator.share({ files: [soubor], title: `QR platba ${data.kod}` });
                return;
            }
        } catch (e) {
            if (e?.name === 'AbortError') return;
        }
        const a = document.createElement('a');
        a.href = obrazek;
        a.download = nazev;
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    const kopiruj = async (klic, hodnota) => {
        try {
            await navigator.clipboard.writeText(hodnota);
            setZkopirovano(klic);
            setTimeout(() => setZkopirovano(k => (k === klic ? null : k)), 1500);
        } catch { /* schránka nemusí být povolená — nevadí */ }
    };

    const zadano = async () => {
        setZadavam(true);
        await onPlatbaZadana?.();
        setZadavam(false);
        onClose?.();
    };

    const radky = data ? [
        { klic: 'prijemce', popis: 'Příjemce', hodnota: data.prijemce },
        { klic: 'ucet', popis: 'Účet', hodnota: data.ucet || fmtIban(data.iban), kopie: data.ucet || data.iban },
        data.ucet && data.iban && { klic: 'iban', popis: 'IBAN', hodnota: fmtIban(data.iban), kopie: data.iban },
        { klic: 'castka', popis: 'Částka', hodnota: `${fmt(data.castka)} ${data.mena}`, kopie: String(data.castka), silne: true },
        { klic: 'vs', popis: 'Variabilní symbol', hodnota: data.vs, kopie: data.vs },
        data.ks && { klic: 'ks', popis: 'Konstantní symbol', hodnota: data.ks, kopie: data.ks },
        data.ss && { klic: 'ss', popis: 'Specifický symbol', hodnota: data.ss, kopie: data.ss },
        { klic: 'zprava', popis: 'Zpráva', hodnota: data.zprava, kopie: data.zprava },
        { klic: 'splatnost', popis: 'Splatnost', hodnota: data.splatnost ? fmtDate(data.splatnost) : null },
    ].filter(Boolean) : [];

    const lzeZadat = ['schvaleny', 'ceka_na_platbu'].includes(data?.stav) && !data?.platba_zadana_at;

    return (
        <ModalShell open onClose={onClose} isDark={isDark} sirka={520}
            nadpis="QR platba" ikona={QrCode} barva={SECTION_COLOR}
            paticka={(
                <div className="flex items-center gap-2">
                    <span className="text-[11px] flex-1" style={{ color: 'var(--text-tertiary)' }}>
                        {data?.platba_zadana_at
                            ? `Zadáno do banky ${fmtDate(data.platba_zadana_at)}.`
                            : 'Po odeslání v bance klikni na „Zadal jsem".'}
                    </span>
                    <button type="button" onClick={onClose}
                        className="px-3 py-1.5 rounded-lg text-xs"
                        style={{ color: 'var(--text-secondary)', border: `1px solid ${hranice}`, background: 'transparent', minHeight: 36 }}>
                        Zavřít
                    </button>
                    {lzeZadat && (
                        <button type="button" onClick={zadano} disabled={zadavam}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
                            style={{ background: OK, opacity: zadavam ? 0.5 : 1, minHeight: 36 }}>
                            {zadavam ? <Loader2 size={13} className="animate-spin" /> : <SendHorizontal size={13} />}
                            Zadal jsem
                        </button>
                    )}
                </div>
            )}>

            {!data && !chyba && (
                <div className="flex items-center justify-center py-10">
                    <Loader2 size={20} className="animate-spin" style={{ color: SECTION_COLOR }} />
                </div>
            )}

            {chyba && (
                <div className="text-[12px] mb-3" style={{ color: WARN }}>{chyba}</div>
            )}

            {data && (
                <>
                    {obrazek ? (
                        <div className="flex flex-col items-center mb-3">
                            <img src={obrazek} alt={`QR platba ${data.kod}`}
                                style={{ width: 240, height: 240, borderRadius: 12, background: '#fff', imageRendering: 'pixelated' }} />
                            <button type="button" onClick={sdilejQr}
                                className="inline-flex items-center justify-center gap-2 mt-3 px-4 rounded-xl text-[13px] font-semibold"
                                style={{
                                    minHeight: 44,
                                    color: dotykove ? '#1A1206' : 'var(--text-primary)',
                                    background: dotykove ? '#FF8A00' : 'transparent',
                                    border: dotykove ? 'none' : `1px solid ${hranice}`,
                                }}>
                                {dotykove ? <Share2 size={16} /> : <Download size={16} />}
                                {dotykove ? 'Uložit nebo sdílet QR' : 'Stáhnout QR'}
                            </button>
                            {dotykove && (
                                <div className="text-[11.5px] mt-2 text-center" style={{ color: 'var(--text-secondary)', lineHeight: 1.45, maxWidth: 320 }}>
                                    Ulož obrázek do fotek a v bankovní appce zvol QR platbu → načíst z galerie.
                                </div>
                            )}
                        </div>
                    ) : !data.spayd && (
                        <div className="rounded-xl p-3 mb-3" style={{ background: `${WARN}14`, border: `1px solid ${WARN}44` }}>
                            <div className="flex items-start gap-2">
                                <AlertTriangle size={15} style={{ color: WARN, flexShrink: 0, marginTop: 1 }} />
                                <div className="text-[12px]" style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                                    <b style={{ color: WARN }}>QR kód nejde sestavit.</b><br />
                                    {data.chyby?.[0]}
                                </div>
                            </div>
                        </div>
                    )}

                    {data.spayd && data.chyby?.length > 0 && (
                        <div className="text-[11.5px] mb-3 flex items-start gap-1.5" style={{ color: WARN, lineHeight: 1.45 }}>
                            <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 2 }} />
                            <span>{data.chyby.join(' ')}</span>
                        </div>
                    )}

                    <div className="rounded-xl" style={{ border: `1px solid ${hranice}` }}>
                        {radky.map((r, i) => (
                            <div key={r.klic} className="flex items-center gap-3 px-3"
                                style={{ minHeight: 40, borderTop: i ? `1px solid ${hranice}` : 'none' }}>
                                <span className="text-[12px] flex-shrink-0" style={{ color: 'var(--text-secondary)', width: 118 }}>{r.popis}</span>
                                <span className={`text-[12.5px] flex-1 min-w-0 truncate tabular-nums ${r.silne ? 'font-bold' : ''}`}
                                    style={{ color: r.hodnota ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                                    {r.hodnota || '—'}
                                </span>
                                {r.kopie && (
                                    <button type="button" onClick={() => kopiruj(r.klic, r.kopie)}
                                        aria-label={`Zkopírovat ${r.popis.toLowerCase()}`}
                                        className="flex items-center justify-center rounded-lg flex-shrink-0"
                                        style={{ width: 32, height: 32, color: zkopirovano === r.klic ? OK : 'var(--text-tertiary)' }}>
                                        {zkopirovano === r.klic ? <Check size={14} /> : <Copy size={14} />}
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="text-[11.5px] mt-3" style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                        Kód <b style={{ color: 'var(--text-primary)' }}>{data.kod}</b> na začátku zprávy nemaž —
                        podle něj se platba po nahrání výpisu sama spáruje s tímhle dokladem.
                    </div>
                </>
            )}
        </ModalShell>
    );
}
